export type Rgb = readonly [number, number, number];

export const BLACK_CHANNELS = '0 0% 0%';
export const WHITE_CHANNELS = '0 0% 100%';

const BLACK: Rgb = [0, 0, 0];
const WHITE: Rgb = [1, 1, 1];

export const hslChannelsToRgb = (channels: string): Rgb => {
  const [hue = 0, saturation = 0, lightness = 0] = channels
    .replace(/%/g, '')
    .trim()
    .split(/\s+/)
    .map(Number);
  const s = saturation / 100;
  const l = lightness / 100;
  const chroma = s * Math.min(l, 1 - l);
  const channel = (offset: number) => {
    const k = (offset + hue / 30) % 12;
    return l - chroma * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [channel(0), channel(8), channel(4)];
};

const linear = (value: number) =>
  value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;

export const relativeLuminance = ([r, g, b]: Rgb): number =>
  0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);

export const contrastRatio = (first: Rgb, second: Rgb): number => {
  const a = relativeLuminance(first);
  const b = relativeLuminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

export const hslContrast = (first: string, second: string): number =>
  contrastRatio(hslChannelsToRgb(first), hslChannelsToRgb(second));

export const readableForeground = (fill: Rgb): string =>
  contrastRatio(fill, BLACK) >= contrastRatio(fill, WHITE)
    ? BLACK_CHANNELS
    : WHITE_CHANNELS;
